// metadataSlice.ts

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

type BulkGeneratedPreview = {
  codebaseId: string;
  fileHash: string;
  previewUrl: string;
  componentName: string;
  filename: string;
  generatedAt: number;
};

type ComponentFailure = {
  codebaseId: string;
  componentName: string;
  filename: string;
  errorMessage: string;
  failedAt: number;
};

type QueueFailureState = {
  hasFailures: boolean;
  consecutiveFailures: number;
  lastFailureMessage?: string;
  failedAt?: number;
  failedComponents: ComponentFailure[];
};

type MetadataState = {
  elementsToFetch: string[];
  isFetching: boolean;
  regenerateEntireQueue: boolean;
  processingInformation: Record<string, { force?: boolean }>;
  bulkGenerated: BulkGeneratedPreview[];
  queueFailureState: QueueFailureState;
};

const initialState: MetadataState = {
  elementsToFetch: [],
  isFetching: false,
  regenerateEntireQueue: false,
  processingInformation: {},
  bulkGenerated: [],
  queueFailureState: {
    hasFailures: false,
    consecutiveFailures: 0,
    failedComponents: [],
  },
};

const metadataSlice = createSlice({
  name: "metadata",
  initialState,
  reducers: {
    initializeQueue: (state, action: PayloadAction<string[]>) => {
      state.elementsToFetch = action.payload;
    },
    dequeueElement: (state) => {
      const elementId = state.elementsToFetch[0];
      if (elementId && state.processingInformation[elementId]) {
        delete state.processingInformation[elementId];
      }
      state.elementsToFetch = state.elementsToFetch.slice(1);
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    enqueueElementsForScreenshotGeneration: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.elementsToFetch = [...state.elementsToFetch, ...action.payload];
    },
    setRegenerateEntireQueue: (state, action: PayloadAction<boolean>) => {
      state.regenerateEntireQueue = action.payload;
    },
    markElementsWithForce: (state, action: PayloadAction<string[]>) => {
      for (const elementId of action.payload || []) {
        if (state.processingInformation[elementId]) {
          state.processingInformation[elementId].force = true;
        } else {
          state.processingInformation[elementId] = { force: true };
        }
      }
    },
    startBulkGeneration: (state, action: PayloadAction<string[]>) => {
      state.bulkGenerated = [];
      // Reset failure state when starting new bulk generation
      state.queueFailureState = {
        hasFailures: false,
        consecutiveFailures: 0,
        failedComponents: [],
      };
    },
    addBulkGeneratedPreview: (
      state,
      action: PayloadAction<BulkGeneratedPreview>
    ) => {
      // Prevent duplicates by checking if this codebaseId already exists
      const existingIndex = state.bulkGenerated.findIndex(
        (existing) => existing.codebaseId === action.payload.codebaseId
      );

      if (existingIndex === -1) {
        // New preview, add it
        state.bulkGenerated.push(action.payload);
      } else {
        // Update existing preview with latest data
        state.bulkGenerated[existingIndex] = action.payload;
      }
    },

    clearBulkGenerated: (state) => {
      state.bulkGenerated = [];
    },
    clearQueue: (state) => {
      state.elementsToFetch = [];
      state.isFetching = false;
      state.processingInformation = {};
      // Reset failure state when clearing queue
      state.queueFailureState = {
        hasFailures: false,
        consecutiveFailures: 0,
        failedComponents: [],
      };
    },
    incrementFailureCount: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.queueFailureState.consecutiveFailures += 1;
      state.queueFailureState.hasFailures = true;
      if (action.payload) {
        state.queueFailureState.lastFailureMessage = action.payload;
      }
      state.queueFailureState.failedAt = Date.now();
    },
    addComponentFailure: (state, action: PayloadAction<ComponentFailure>) => {
      state.queueFailureState.failedComponents.push(action.payload);
      state.queueFailureState.hasFailures = true;
    },
    resetFailureCount: (state) => {
      state.queueFailureState.consecutiveFailures = 0;
      // Keep hasFailures true if there were any failures during this session
      // Only reset it when starting new generation or clearing queue
    },
  },
});

export const {
  initializeQueue,
  dequeueElement,
  setIsFetching,
  enqueueElementsForScreenshotGeneration,
  setRegenerateEntireQueue,
  markElementsWithForce,
  startBulkGeneration,
  addBulkGeneratedPreview,
  clearBulkGenerated,
  clearQueue,
  incrementFailureCount,
  addComponentFailure,
  resetFailureCount,
} = metadataSlice.actions;

export type { BulkGeneratedPreview, ComponentFailure };
export default metadataSlice.reducer;
